import { useCallback, useLayoutEffect, useRef, useSyncExternalStore } from 'react';

import { getHasOpenModal, registerOpenModal, subscribeToOpenModals } from './utils';

/** True while at least one modal has finished opening. Use it for overlays outside the dialog. */
export const useHasOpenModal = (): boolean =>
    useSyncExternalStore(subscribeToOpenModals, getHasOpenModal, getHasOpenModal);

/**
 * Registers the modal after its panel enter animation ends.
 * Unregisters as soon as `open` becomes false, so overlays hide when the leave starts.
 * Does no work on the open layout path — that would delay Headless UI's slide.
 */
export const useRegisterOpenModalAfterEnter = (open: boolean): (() => void) => {
    const unregisterRef = useRef<(() => void) | null>(null);

    const onAfterEnter = useCallback(() => {
        if (unregisterRef.current) {
            return;
        }
        unregisterRef.current = registerOpenModal();
    }, []);

    useLayoutEffect(() => {
        if (open) {
            return undefined;
        }
        unregisterRef.current?.();
        unregisterRef.current = null;
        return undefined;
    }, [open]);

    useLayoutEffect(() => {
        return () => {
            unregisterRef.current?.();
            unregisterRef.current = null;
        };
    }, []);

    return onAfterEnter;
};
